import { ModalTransition } from '@atlaskit/modal-dialog';
import { ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import SiteSelectorDialog from '@atlassian/mpac-site-selection';
import {
  type MarketplaceStoreEditionType,
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/graphql-gateway-types';
import capitalize from 'lodash/capitalize';
import React from 'react';

import {
  safeToForwardToPartner,
  trackInstallClick,
  trackRequestClick,
} from '../../../../../../../../../common/utils/analytics/analytics';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
  ANALYTICS_SUBJECT_ID_CTA,
  ANALYTICS_SUBJECT_ID_REQUEST_APP,
} from '../../../../../../../../analytics/Events';
import { STARGATE_BASE_PATH } from '../../../../../../../../constants';

import type { MarketplaceProduct, SelectedSiteAndProduct } from '@atlassian/mpac-site-selection';
import type { AppDetailsLink } from '@atlassian/mpac-types';
import type { SyntheticEvent } from 'react';

export type Props = {
  appId: string;
  appCompatibilities: MarketplaceProduct[];
  appKey: string;
  appName: string;
  paymentModel: MarketplaceAppPaymentModel;
  appDetailsLink: AppDetailsLink;
  installAppHrefResolver: (
    arg1: SelectedSiteAndProduct,
    arg2?: boolean,
    plan?: MarketplaceStoreEditionType
  ) => string;
  requestAppHrefResolver: (
    arg1: SelectedSiteAndProduct,
    plan?: MarketplaceStoreEditionType
  ) => string;
  onClose: () => void;
  isOpen: boolean;
  loginRedirectPath: string;
  mpacBaseUrl: string;
  showAppPlans: boolean;
  selectedPlanContext?: MarketplaceStoreEditionType;
  appPricingDetailsLink: string;
  editionTypeList: MarketplaceStoreEditionType[];
};

const SiteSelectorDialogWrapper = (props: Props) => {
  const fireAnalyticsEvent = useAnalyticsEvents();

  const handleSiteSelectorInstallClick = (
    // @ts-expect-error [MC-2850] - TS6133 - 'isAppFree' is declared but its value is never read.
    isAppFree: boolean,
    event: SyntheticEvent<HTMLLinkElement>,
    selectedPlan?: MarketplaceStoreEditionType
  ) => {
    event.preventDefault();
    const { currentTarget } = event;
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        hosting: 'cloud',
        label: 'Start Free Trial',
        source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        category: ANALYTICS_CATEGORY_APP_LISTING,
        ...(props.showAppPlans && { selectedPlan }),
      },
    });

    safeToForwardToPartner(props.appId, () => {
      trackInstallClick(capitalize(AtlassianProductHostingType.Cloud), selectedPlan);
    });

    if (currentTarget instanceof HTMLAnchorElement) {
      // @ts-expect-error [MC-2850] - TS2322 - Type 'string' is not assignable to type '(string | Location) & Location'.
      window.location = currentTarget.href;
    }
  };

  const handleRequestAppClick = (_, selectedPlan?: MarketplaceStoreEditionType) => {
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_REQUEST_APP,
      source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        label: 'Request App',
        category: ANALYTICS_CATEGORY_APP_LISTING,
        hosting: 'cloud',
        source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        ...(props.showAppPlans && { selectedPlan }),
      },
    });
    safeToForwardToPartner(props.appId, () => {
      trackRequestClick(capitalize(AtlassianProductHostingType.Cloud), selectedPlan);
    });
  };

  return (
    <ModalTransition>
      {props.isOpen && (
        <SiteSelectorDialog
          appCompatibilities={props.appCompatibilities}
          stargateBaseUrl={STARGATE_BASE_PATH}
          installAppHrefResolver={props.installAppHrefResolver}
          requestAppHrefResolver={props.requestAppHrefResolver}
          loginHref={`/login?redirect=${encodeURIComponent(props.loginRedirectPath)}`}
          onClose={props.onClose}
          isFreeApp={props.paymentModel === MarketplaceAppPaymentModel.Free}
          onInstallClick={handleSiteSelectorInstallClick}
          onRequestAppClick={handleRequestAppClick}
          mpacBaseUrl={props.mpacBaseUrl}
          showAppPlans={props.showAppPlans}
          selectedPlanContext={props.selectedPlanContext}
          appPricingDetailsLink={props.appPricingDetailsLink}
          editionTypeList={props.editionTypeList}
          appKey={props.appKey}
        />
      )}
    </ModalTransition>
  );
};

export default SiteSelectorDialogWrapper;
